import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { UploadS3Input } from "design-system/organisms/UploadS3Input"
import { cn } from "design-system/utils"
import { File, GripVertical, Music, Paperclip, Trash } from "lucide-react"
import { useUploadGoalMediaMutation } from "modules/campaigns/hooks/useUploadGoalMediaMutation"
import { Loading } from "modules/shared/components/Loading"
import { useFieldArray, UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { CreateGoalSchema, GoalMediaSchema } from "schema"
import z from "zod"

type GoalMediaProps = {
  form: UseFormReturn<z.infer<typeof CreateGoalSchema>>
}

type GoalMediaCardProps = {
  id: string
  item: z.infer<typeof GoalMediaSchema>
  onRemove: () => void
  onLabelChange: (label: string) => void
}

type GetMediaElementProps = {
  item: z.infer<typeof GoalMediaSchema>
  small?: boolean
}

export const GetMediaElement = ({ item, small }: GetMediaElementProps) => {
  switch (item.mediaType) {
    case "IMAGE":
    case "GIF":
      return (
        <Tooltip>
          <TooltipTrigger>
            <img
              src={item.link}
              className="w-26 max-h-14 h-14 object-cover block rounded-sm border"
            />
          </TooltipTrigger>
          <TooltipContent className="p-2">
            <img
              src={item.link}
              className={cn("rounded-sm", small ? "max-h-[160px]" : "max-h-[300px]")}
            />
          </TooltipContent>
        </Tooltip>
      )
    case "VIDEO":
      return (
        <Tooltip>
          <TooltipTrigger>
            <video
              preload="metadata"
              src={item.link}
              className="w-26 max-h-14 h-14 object-cover block rounded-sm border"
            />
          </TooltipTrigger>
          <TooltipContent className="p-2">
            <video
              controls
              src={item.link}
              autoPlay
              className={cn("rounded-sm", small ? "max-h-[160px]" : "max-h-[300px]")}
            />
          </TooltipContent>
        </Tooltip>
      )
    case "AUDIO":
      return (
        <Tooltip>
          <TooltipTrigger>
            <div className="w-21 h-14 items-center flex justify-center border rounded-sm text-muted-foreground">
              <Music size={24} />
            </div>
          </TooltipTrigger>
          <TooltipContent className="p-2">
            <audio controls src={item.link} autoPlay />
          </TooltipContent>
        </Tooltip>
      )
    case "DOCUMENT":
      return (
        <Tooltip>
          <TooltipTrigger>
            <div className="w-21 h-14 items-center flex justify-center border rounded-sm text-muted-foreground">
              <File size={24} />
            </div>
          </TooltipTrigger>
          <TooltipContent className="p-2">
            <iframe src={item.link} className="h-[200px]" />
          </TooltipContent>
        </Tooltip>
      )
  }
}

const GoalMediaCard = ({ id, item, onRemove, onLabelChange }: GoalMediaCardProps) => {
  const { t } = useTranslation()
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="flex group/media hover:bg-muted rounded-md py-2 pr-2 items-center"
    >
      <div className="flex gap-2 grow items-center">
        <div className="min-w-4">
          <GripVertical className="size-4 cursor-grab text-muted-foreground" size={24} />
        </div>
        <div className="flex gap-2 grow items-center">
          <div className="grow flex items-center">
            <GetMediaElement item={item} />
          </div>
          <Input
            className="grow"
            value={item.label ?? ""}
            onKeyDown={(ev) => ev.stopPropagation()}
            onChange={(ev) => {
              ev.preventDefault()
              onLabelChange(ev.target.value)
            }}
            onPointerDown={(e) => e.stopPropagation()}
            placeholder={t("shared.labelPlaceholder")}
          />
        </div>
        <Button
          type="button"
          variant={"outline"}
          size={"icon"}
          className="opacity-0 group-hover/media:opacity-100"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(ev) => {
            ev.stopPropagation()
            onRemove()
          }}
        >
          <Trash />
        </Button>
      </div>
    </div>
  )
}

export const GoalMedia = ({ form }: GoalMediaProps) => {
  const { t } = useTranslation()
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )
  const { mutateAsync, isPending } = useUploadGoalMediaMutation()

  const { fields, append, remove, replace, update } = useFieldArray({
    control: form.control,
    name: "goalMedias",
  })
  const fieldsArray = fields.map((e) => e.id)

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = fieldsArray.indexOf(String(active.id))
      const newIndex = fieldsArray.indexOf(String(over.id))

      replace(arrayMove(fields, oldIndex, newIndex))
    }
  }

  return (
    <FormField
      control={form.control}
      name="goalMedias"
      render={() => (
        <FormItem>
          <FormLabel>{t("campaigns.goalsManagement.modal.fields.media.title")}</FormLabel>
          <FormLabel className="text-xs text-muted-foreground">
            {t("campaigns.goalsManagement.modal.fields.media.description")}
          </FormLabel>
          <FormControl>
            <>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onDragEnd}
              >
                <SortableContext items={fields} strategy={verticalListSortingStrategy}>
                  <div>
                    {fields.map(({ id, ...item }, index) => (
                      <GoalMediaCard
                        key={item.link}
                        item={item}
                        id={id}
                        onRemove={() => remove(index)}
                        onLabelChange={(label) => update(index, { ...item, label })}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
              <UploadS3Input
                onChange={(media) => {
                  if (media.url && media.format) {
                    append({ link: media.url, mediaType: media.format, order: fields.length })
                  }
                }}
                placeholder={t(
                  "campaigns.templates.widget.config.appearance.bubbleImagePlaceholder",
                )}
                mutateAsync={mutateAsync}
                isPending={isPending}
                disabled={isPending}
                accept=".png,.jpg,.jpeg,.webp,.gif,.mp3,.wav,.ogg,.pdf"
                component={(isPending) => (
                  <Button variant={"outline"} type="button" disabled={isPending}>
                    {isPending ? <Loading minimal /> : <Paperclip size={16} />}
                    <span>{t("campaigns.goalsManagement.modal.fields.media.addMedia")}</span>
                  </Button>
                )}
              />
            </>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
